import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import Backimg from "../../../assets/img/backicon01.svg";
import Cancelimg from "../../../assets/img/cancel-01.svg";
import { KycDocLoader } from "../../../components/Onboarding/KycDocLoader";
import useTranslator from "../../../hooks/useTranslator";
import KycVerificationPopup from "../KycVerificationPopup";
import { COUNTRIES, getDocumentsForCountry } from "./documentSelection";
import { KYC_STEP, useKycWorkflow } from "./hooks/useKycWorkflow";

import "./authenticateYourId.css";
import ContinueOnPhoneScreen from "./components/ContinueOnPhoneScreen";
import DocumentSelectScreen from "./components/DocumentSelectScreen";
import FileUploadScreen from "./components/FileUploadScreen";
import LoadingScreen from "./components/LoadingScreen";
import VerificationFailedModal from "./components/VerificationFailed";
import VerificationStartScreen from "./components/VerificationStartScreen";
import CaptureScreen from "../../mobile-capture/components/CaptureScreen";

interface AuthenticateYourIdProps {
  user: { _id: string; [key: string]: any };
  formHandler: { prev: () => void };
  updateUserProfile: (data: any) => void;
  isGuarantor: boolean;
  usedFor: string;
  setShowKycPopup: (show: boolean) => void;
  addedFiles: any[];
  setAddedFiles: (files: any[]) => void;
  getNextStep: () => number;
  isKycSkippable?: boolean;
  onSkipKyc?: () => void;
  onProgressChange?: (stage: "document" | "photos" | "processing") => void;
}

const AuthenticateYourId = ({
  user,
  formHandler,
  updateUserProfile,
  isGuarantor,
  usedFor,
  setShowKycPopup,
  setAddedFiles,
  getNextStep,
  isKycSkippable = false,
  onSkipKyc,
  onProgressChange,
}: AuthenticateYourIdProps) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery({ maxWidth: 1024 });

  const {
    failedKyc,
    setFailedKyc,
    failedWorkflowResult,
    customKycStarted,
    onfidoInitiateLoading,
    kycInitiationFailed,
    kycVerificationPopup,
    setKycVerificationPopup,
    showOnfidoWatingLoading,
    showKycLoading,
    showDocumentRetryMessage,
    kycStep,
    docType,
    issuingCountry,
    frontImage,
    setFrontImage,
    backImage,
    setBackImage,
    captureSubMode,
    setCaptureSubMode,
    needsBackSide,
    startCustomKyc,
    handleStartVerification,
    handleCreateMobileSession,
    handleCustomKycCancel,
    handleDocTypeChange,
    handleCountryChange,
    handleDocTypeSelect,
    handleFileUploadFront,
    handleFileUploadBack,
    handleFrontCapture,
    handleBackCapture,
    handleMobileCancel,
    mobileSessionData,
    mobileSessionState,
    loadUser,
    setKycStep,
  } = useKycWorkflow({
    user,
    isGuarantor,
    usedFor,
    setShowKycPopup,
    setAddedFiles,
    getNextStep,
    updateUserProfile,
  });

  useEffect(() => {
    if (!onProgressChange) return;

    if (
      kycStep === KYC_STEP.UPLOADING ||
      kycStep === KYC_STEP.PROCESSING ||
      (kycStep === KYC_STEP.CONTINUE_ON_PHONE &&
        mobileSessionState === "SUBMITTED")
    ) {
      onProgressChange("processing");
    } else if (
      kycStep === KYC_STEP.CAPTURE_FRONT ||
      kycStep === KYC_STEP.CAPTURE_BACK ||
      kycStep === KYC_STEP.CONTINUE_ON_PHONE
    ) {
      onProgressChange("photos");
    } else {
      onProgressChange("document");
    }
  }, [kycStep, mobileSessionState, onProgressChange]);

  const getTranslatedText = (key: string, fallback: string) => {
    return _t(key);
  };

  const getDocumentUploadTitle = () => {
    if (docType === "passport") {
      return getTranslatedText("uploadYourPassport", "Upload your passport");
    }
    if (docType === "residence_permit") {
      return getTranslatedText(
        "uploadYourResidencePermit",
        "Upload your residence permit"
      );
    }
    return getTranslatedText(
      "uploadYourNationalIdentityCard",
      "Upload your ID"
    );
  };

  const getDocumentUploadSubtitle = (side: "front" | "back") => {
    if (docType === "passport") {
      return getTranslatedText(
        "passportPhotoPageHint",
        "We need the photo page, complete and legible"
      );
    }
    if (side === "back") {
      return getTranslatedText(
        "nowUploadDocumentBack",
        "Now upload the back of your document."
      );
    }
    return getTranslatedText(
      "startWithDocumentFront",
      "Start with the front. We'll ask for the back later."
    );
  };

  const renderCaptureStep = ({
    title,
    subtitle,
    side,
    onFileUpload,
    onContinueOnPhone,
    onTakePhoto,
  }: {
    title: string;
    subtitle: string;
    side: "front" | "back";
    onFileUpload: (file: File | Blob) => Promise<void>;
    onContinueOnPhone?: () => void;
    onTakePhoto?: () => void;
  }) => {
    return (
      <FileUploadScreen
        key={kycStep}
        title={title}
        subtitle={subtitle}
        docType={docType}
        side={side}
        showStepBadge={needsBackSide}
        onFileSelect={onFileUpload}
        onContinueOnPhone={onContinueOnPhone}
        onTakePhoto={onTakePhoto}
      />
    );
  };

  const getCustomKycBackHandler = () => {
    if (kycStep === KYC_STEP.CAPTURE_FRONT) {
      return () => setKycStep(KYC_STEP.SELECT_DOC);
    }

    if (kycStep === KYC_STEP.CAPTURE_BACK) {
      return () => setKycStep(KYC_STEP.CAPTURE_FRONT);
    }

    if (kycStep === KYC_STEP.CONTINUE_ON_PHONE) {
      return handleMobileCancel;
    }

    return null;
  };

  const renderCustomKycStep = () => {
    const availableDocs = getDocumentsForCountry(issuingCountry);

    if (
      captureSubMode === "camera" &&
      (kycStep === KYC_STEP.CAPTURE_FRONT || kycStep === KYC_STEP.CAPTURE_BACK)
    ) {
      const isBack = kycStep === KYC_STEP.CAPTURE_BACK;
      return (
        <CaptureScreen
          side={isBack ? "back" : "front"}
          onCapture={isBack ? handleBackCapture : handleFrontCapture}
          onCancel={() => setCaptureSubMode(null)}
        />
      );
    }

    switch (kycStep) {
      case KYC_STEP.START:
        return <VerificationStartScreen onStart={handleStartVerification} />;

      case KYC_STEP.SELECT_DOC:
        return (
          <DocumentSelectScreen
            issuingCountry={issuingCountry}
            docType={docType}
            availableCountries={COUNTRIES}
            availableDocs={availableDocs}
            onCountryChange={handleCountryChange}
            onDocTypeChange={handleDocTypeChange}
            onContinue={handleDocTypeSelect}
            showRetryMessage={showDocumentRetryMessage}
            _t={_t}
          />
        );

      case KYC_STEP.CAPTURE_FRONT:
        return renderCaptureStep({
          title: getDocumentUploadTitle(),
          subtitle: getDocumentUploadSubtitle("front"),
          side: "front",
          onFileUpload: handleFileUploadFront,
          onContinueOnPhone: isMobileOrTablet
            ? undefined
            : handleCreateMobileSession,
          onTakePhoto: isMobileOrTablet
            ? () => setCaptureSubMode("camera")
            : undefined,
        });

      case KYC_STEP.CAPTURE_BACK:
        return renderCaptureStep({
          title: getDocumentUploadTitle(),
          subtitle: getDocumentUploadSubtitle("back"),
          side: "back",
          onFileUpload: handleFileUploadBack,
          onTakePhoto: isMobileOrTablet
            ? () => setCaptureSubMode("camera")
            : undefined,
        });

      case KYC_STEP.UPLOADING:
        return <LoadingScreen message={_t("uploadingDocuments")} />;

      case KYC_STEP.CONTINUE_ON_PHONE:
        if (!mobileSessionData) {
          return <LoadingScreen message={_t("creatingMobileSession")} />;
        }
        if (mobileSessionState === "SUBMITTED") {
          return (
            <LoadingScreen
              message={
                showOnfidoWatingLoading
                  ? _t("pleaseWaitWhileWeAreFetchingYourKycDetails")
                  : _t("processingYourVerification")
              }
            />
          );
        }
        return (
          <ContinueOnPhoneScreen
            qrUrl={mobileSessionData.qrUrl}
            copyUrl={mobileSessionData.copyUrl}
            sessionState={mobileSessionState}
            onCancel={handleMobileCancel}
          />
        );

      case KYC_STEP.PROCESSING:
        return (
          <LoadingScreen
            message={
              showOnfidoWatingLoading
                ? _t("pleaseWaitWhileWeAreFetchingYourKycDetails")
                : _t("processingYourVerification")
            }
          />
        );

      default:
        return null;
    }
  };

  const renderKycInitiationFailed = () => (
    <div className="custom-kyc-init-failed" role="alert">
      <div className="custom-kyc-init-failed-icon">!</div>
      <h3>{_t("kycInitializationFailed")}</h3>
      <p>{_t("kycInitializationFailedDescription")}</p>
      <button
        type="button"
        className="btnNext kyc-verification-start-button"
        disabled={onfidoInitiateLoading}
        onClick={startCustomKyc}
      >
        {_t("retryInitialization")}
      </button>
    </div>
  );

  const customKycBackHandler = getCustomKycBackHandler();

  return (
    <>
      {customKycStarted && (
        <div className="onfido-overlay">
          <div className="onfido-wrapper">
            <div className="custom-kyc-modal-toolbar">
              {customKycBackHandler ? (
                <button
                  type="button"
                  className="custom-kyc-toolbar-button"
                  onClick={customKycBackHandler}
                  aria-label={_t("back")}
                >
                  <img src={Backimg} alt="" />
                </button>
              ) : (
                <span className="custom-kyc-toolbar-spacer" />
              )}
              <div className="custom-kyc-toolbar-right">
                {isKycSkippable && onSkipKyc && (
                  <button
                    type="button"
                    className="custom-kyc-toolbar-button custom-kyc-toolbar-skip"
                    onClick={onSkipKyc}
                  >
                    {_t("skip")}
                  </button>
                )}
                {usedFor === "popup" && kycStep !== KYC_STEP.PROCESSING ? (
                  <button
                    type="button"
                    className="custom-kyc-toolbar-button custom-kyc-toolbar-close"
                    onClick={handleCustomKycCancel}
                    aria-label={_t("close")}
                  >
                    <img src={Cancelimg} alt="" />
                  </button>
                ) : !isKycSkippable ? (
                  <span className="custom-kyc-toolbar-spacer" />
                ) : null}
              </div>
            </div>
            <div className="custom-kyc-container">
              {onfidoInitiateLoading ? (
                <LoadingScreen message={_t("initializingKyc")} />
              ) : kycInitiationFailed ? (
                renderKycInitiationFailed()
              ) : (
                <>{renderCustomKycStep()}</>
              )}
            </div>
          </div>
        </div>
      )}
      {!failedKyc && (
        <div
          className={`approvedModal popup-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 wrapper-modal modal-fixed-screen ${
            isGuarantor ? "guarantorBg" : ""
          }`}
          style={{ zIndex: 99 }}
        >
          {!customKycStarted && (
            <div
              className="popup-content2 position-relative popup-content bg-white required-doc-popup onfiddoModal authenticate-your-id-modal authenticate-your-id-welcome"
              onClick={isGuarantor ? (e) => e.stopPropagation() : undefined}
            >
              <div className="authenticate-your-id-header">
                {isKycSkippable && onSkipKyc && (
                  <button
                    type="button"
                    className="authenticate-your-id-skip"
                    onClick={onSkipKyc}
                  >
                    {_t("skip")}
                  </button>
                )}
                {(usedFor === "popup" || isGuarantor) && (
                  <button
                    type="button"
                    className="authenticate-your-id-close"
                    onClick={
                      usedFor === "popup"
                        ? () => setShowKycPopup(false)
                        : () => navigate("/home")
                    }
                    aria-label={_t("close")}
                  >
                    <img src={Cancelimg} alt="" />
                  </button>
                )}
              </div>
              <VerificationStartScreen
                onStart={startCustomKyc}
                isLoading={onfidoInitiateLoading}
              />
            </div>
          )}
        </div>
      )}
      <KycVerificationPopup
        showPopup={kycVerificationPopup}
        setKycVerificationPopup={setKycVerificationPopup}
        loadUser={loadUser}
        isGuarantor={isGuarantor}
      />
      <KycDocLoader isGuarantor={isGuarantor} showPopup={showKycLoading} />
      <VerificationFailedModal
        showPopup={failedKyc}
        isGuarantor={isGuarantor}
        heading={_t("could_not_verify_your_documents")}
        subheading={_t("you_can_try_again")}
        closeModalState={setFailedKyc}
        hideBackgroundImage={undefined}
        buttonText={undefined}
        setKycStep={setKycStep}
        workflowResult={failedWorkflowResult}
      />
    </>
  );
};

export default AuthenticateYourId;
